/**
 * utils/dateUtils.ts
 *
 * Utility functions for date formatting and manipulation.
 * Updated: added formatDate (day numeric, month long, year numeric, fr-FR)
 * and formatDateNullable for nullable string inputs.
 * Updated Apr 2026: added formatDateWithTime (DD/MM/YYYY à HH:MM, fr-FR).
 */

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatDateNullable(dateString: string | null, fallback = '-'): string {
  if (!dateString) return fallback;
  return formatDate(dateString);
}

export function formatDateWithTime(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  const datePart = date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${datePart} à ${timePart}`;
}

/**
 * Formats a date string into a relative time format in French
 * Examples: "il y a 3 min", "il y a 2 h", "il y a 5 j", "il y a 3 sem.", "il y a 2 mois"
 */
export function formatRelativeDate(dateString: string | null): string {
  if (!dateString) return 'Jamais';

  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInMinutes < 1) {
    return 'À l\'instant';
  } else if (diffInMinutes < 60) {
    return `il y a ${diffInMinutes} min`;
  } else if (diffInHours < 24) {
    return `il y a ${diffInHours} h`;
  } else if (diffInDays < 7) {
    return `il y a ${diffInDays} j`;
  } else if (diffInWeeks < 5) {
    return `il y a ${diffInWeeks} sem.`;
  } else if (diffInMonths < 12) {
    return `il y a ${diffInMonths} mois`;
  } else {
    return `il y a ${diffInYears} ${diffInYears === 1 ? 'an' : 'ans'}`;
  }
}
